import React, { PureComponent, Fragment, Suspense } from 'react';
import PropTypes from 'prop-types';
import Hidden from '@material-ui/core/Hidden';
import { DESKTOP_THIN_GUTTER } from '../../constants/layout';
import { isEmptyObject, safeGet } from '../../core/util/object';
import { biggerThanOrEqualTo, breakpoints } from '../../core/util/styledComponents';
import { LOGGED_OUT_FIND_CARE_VIEW } from '../Account/util/views';
import DownloadDriver from '../DownloadDriver';
import Desktop from './Desktop';
import Mobile from './Mobile';
import CDPInfoFactory from './CDPInfoFactory';
import { getDefaultBreakpoint } from '../../core/util/device/screen';
import styled from 'styled-components';
import Layout from '../SolvPatternLibrary/Layout';
import {
  analyticsTagHotjarRecording,
  analyticsTrackEvent,
  analyticsTriggerHotjarRecording,
} from '../../core/analytics';
import {
  CDP_PHOTO_CAROUSEL_INTERACTION,
  CDP_LOADED_CDP,
  campaignNames,
} from '../../core/analytics/events';
import OpenGraph from './MetaTags/OpenGraph';
import TwitterCard from './MetaTags/TwitterCard';
import NoIndex from './MetaTags/NoIndex';
import { isEmpty } from '../../core/util/empty';
import Loading from '../SolvPatternLibrary/Loading';
import { getCurrentPath, getQueryParams } from '../../core/util/url';
import { colors } from '../../constants/colors';
import {
  getLocationSpecialtyValue,
  isBookable,
  isInDfwMarket,
  isSolvPartner,
} from '../../core/util/location';
import { CDP_CLICK_TO_CALL_MODAL } from './Book/components/ClickToCall';
import Canonical, { CanonicalSourceType } from '../MetaTags/Canonical';
import { BOOKING_TIME_SELECTOR_MODAL } from './Book/components/BookModule/Modal';
import { isMobile } from '../../core/util/device';
import TrackImpression from '../../core/tracking/impression/TrackLocationImpression';
import { shouldTrackCDPImpression } from '../../core/tracking/impression/util';
import { MARKETPLACE_DFW } from '../../constants';
import { HOTJAR_TRACKING_LABEL } from '../../core/tracking/hotjar/constants';
import { isClientSide } from '../../core/util/system';
import { FOOTER, getCustomization } from './util/customization';
const LazyGoogleAnalytics = React.lazy(() =>
  import(/* webpackChunkName: 'LazyGoogleAnalytics' */ '../GoogleAnalytics/GoogleAnalytics')
);

const ContentComponent = styled.div`
  background-color: ${colors.theWhiteAlbum};

  ${biggerThanOrEqualTo.md`
    padding: 0 ${DESKTOP_THIN_GUTTER}px;
  `}
`;

const StyledDesktop = styled(Desktop)`
  padding-top: 60px;
  max-width: ${breakpoints.lg};
  margin: 0 auto;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
`;

export default class ClinicDetailPage extends PureComponent {
  constructor(props) {
    super(props);
    const { sessionTrackingProperties } = props;
    const userAgent = safeGet(sessionTrackingProperties)('userAgent');
    this.footerIsVisible = getCustomization(FOOTER, {
      userAgent,
      location: this.props.location,
    }).visible;
  }

  state = {
    photoCarouselOpen: false,
    PhotoCarousel: null,
    ModalController: null,
  };

  componentDidMount = () => {
    this.handleTrackCustomPageLoadEvent();
    if (!this.hasLocationData()) {
      this.props.fetchLocationData(this.props.requestId);
    } else {
      const requestedLocationId = this.props.requestId;
      const locationIdInState = this.props.location.id;
      if (requestedLocationId !== locationIdInState) {
        this.props.fetchLocationData(requestedLocationId);
      }
    }
    this.handleOpenClickToCallModal();
    this.handleOpenCdpBookModal();
  };

  handleOpenClickToCallModal = () => {
    const { location } = this.props;
    const queryParams = getQueryParams();
    const ctcModal = safeGet(queryParams)('ctcModal');

    if (ctcModal && !isSolvPartner(location)) {
      this.loadAndShowModal(CDP_CLICK_TO_CALL_MODAL);
    }
  };

  handleOpenCdpBookModal = () => {
    const { location } = this.props;
    if (!isBookable(location)) {
      return;
    }

    const queryParams = getQueryParams();
    const utmMedium = safeGet(queryParams)('utm_medium');
    const utmSource = safeGet(queryParams)('utm_source');

    if (utmMedium === 'button') {
      switch (utmSource) {
        case 'dir_srp':
        case 'dir_state':
        case 'dir_city':
        case 'dir_nhood':
        case 'dir_zip':
        case 'dir_blog':
        case 'dir_faq':
        case 'dir_a-z':
        case 'dir_near-me':
        case 'dir_brand':
          isMobile() && this.loadAndShowModal(BOOKING_TIME_SELECTOR_MODAL);
          break;
        default:
          return;
      }
    }

    const modalParam = safeGet(queryParams)('modal');
    if (modalParam === 'book') {
      this.loadAndShowModal(BOOKING_TIME_SELECTOR_MODAL);
    }
  };

  handleTrackCustomPageLoadEvent = () => {
    const {
      location,
      location: { id, uclId, city, state, zipCode, market, platformType, isViewable },
      sessionTrackingProperties: { sessionID },
    } = this.props;
    const trackingProperties = {
      path: getCurrentPath(),
      locationId: id,
      uclId,
      city,
      state,
      zipCode,
      partner: isSolvPartner(location),
      isSolvPartner: isSolvPartner(location),
      isBookable: isBookable(location),
      isViewable,
      market,
      platformType,
      trackingPropertiesSessionId: sessionID,
    };

    analyticsTrackEvent(CDP_LOADED_CDP, trackingProperties);
    const specialty = getLocationSpecialtyValue(location);
    const hotjarTags = [HOTJAR_TRACKING_LABEL.cdp, `${HOTJAR_TRACKING_LABEL.cdp}_${specialty}`];

    if (isInDfwMarket(location)) {
      hotjarTags.push(MARKETPLACE_DFW);
      analyticsTriggerHotjarRecording(MARKETPLACE_DFW);
    }
    analyticsTagHotjarRecording(hotjarTags);
  };

  hasLocationData = () => {
    const { location, requestId } = this.props;

    // if location data is missing
    if (!location || isEmptyObject(location) || location.error || !location.id) {
      return false;
    }

    // if location in state doesn't match location in URL
    if (requestId && requestId !== location.id) {
      return false;
    }

    // if location in state doesn't match user profile location
    return !!requestId;
  };

  handleOpenCarousel = section => {
    const {
      location: { id, uclId },
    } = this.props;
    const { PhotoCarousel } = this.state;
    if (isEmpty(PhotoCarousel)) {
      import(/* webpackChunkName: 'photoCarousel', webpackPrefetch: true */ '../SolvPatternLibrary/PhotoCarousel/PhotoCarousel').then(
        module => {
          this.setState({
            PhotoCarousel: module.default,
            photoCarouselOpen: true,
          });
        }
      );
    } else {
      this.setState({ photoCarouselOpen: true });
    }
    analyticsTrackEvent(CDP_PHOTO_CAROUSEL_INTERACTION, {
      interaction: 'open',
      locationId: id,
      uclId,
      section,
    });
  };

  handleCloseCarousel = () => {
    const {
      location: { id },
    } = this.props;
    this.setState({ photoCarouselOpen: false }, () =>
      analyticsTrackEvent(CDP_PHOTO_CAROUSEL_INTERACTION, {
        interaction: 'close',
        locationId: id,
      })
    );
  };

  loadAndShowModal = modalId => {
    const { showModal } = this.props;
    const { ModalController } = this.state;
    if (isEmpty(ModalController)) {
      import(/* webpackChunkName: 'cdpBookModal', webpackPrefetch: true  */ './ModalController').then(
        module => {
          this.setState({ ModalController: module.default }, () => showModal(modalId));
        }
      );
    } else {
      showModal(modalId);
    }
  };

  render = () => {
    if (!this.hasLocationData()) {
      return (
        <Centered>
          <Loading />
        </Centered>
      );
    }

    const {
      location,
      newBooking,
      viewport,
      nearbyLocations,
      modalState,
      hideModal,
      fetchPartnerClinics,
      fetchLocationSlotsData,
      handleRequestAppointmentClick,
      accountSummary,
      login,
      suggestions,
      reasonForVisit,
      setBookingProfile,
      sessionTrackingProperties,
    } = this.props;
    const CDPInfo = CDPInfoFactory.buildFromLocation(location, nearbyLocations);
    const { photoCarouselOpen, PhotoCarousel, ModalController } = this.state;
    this.context.setTitle(CDPInfo.getTitle());
    this.context.setMeta('description', CDPInfo.getDescription());
    this.context.setMeta('keywords', CDPInfo.getKeywords());

    return (
      <Fragment>
        {isClientSide() && (
          <Suspense fallback={null}>
            <LazyGoogleAnalytics location={location} />
          </Suspense>
        )}
        <Canonical location={location} sourceType={CanonicalSourceType.CDP} />
        <OpenGraph location={location} />
        <TwitterCard location={location} />
        <NoIndex location={location} />
        {shouldTrackCDPImpression(this.props) && <TrackImpression />}
        {!isBookable(location) && <DownloadDriver campaignName={campaignNames.NON_PARTNER_CDP} />}
        <Layout
          mobilePadded={false}
          selectedTabId={LOGGED_OUT_FIND_CARE_VIEW}
          ContentComponent={ContentComponent}
          hideFooter={!this.footerIsVisible}
        >
          <Hidden smDown initialWidth={getDefaultBreakpoint()}>
            <StyledDesktop
              newBooking={newBooking}
              showModal={this.loadAndShowModal}
              nearbyLocations={nearbyLocations}
              location={location}
              handleOpenCarousel={this.handleOpenCarousel}
              viewport={viewport}
              handleRequestAppointmentClick={handleRequestAppointmentClick}
              setBookingProfile={setBookingProfile}
              fetchLocationSlotsData={fetchLocationSlotsData}
              accountSummary={accountSummary}
              login={login}
              suggestions={suggestions}
              reasonForVisit={reasonForVisit}
              sessionTrackingProperties={sessionTrackingProperties}
            />
          </Hidden>
          <Hidden mdUp initialWidth={getDefaultBreakpoint()}>
            <Mobile
              showModal={this.loadAndShowModal}
              location={location}
              handleOpenCarousel={this.handleOpenCarousel}
              nearbyLocations={nearbyLocations}
              viewport={viewport}
              accountSummary={accountSummary}
              login={login}
              sessionTrackingProperties={sessionTrackingProperties}
            />
          </Hidden>
          {PhotoCarousel && (
            <PhotoCarousel
              open={photoCarouselOpen}
              handleClose={this.handleCloseCarousel}
              location={location}
              viewport={viewport}
            />
          )}
          {ModalController && (
            <ModalController
              hideModal={hideModal}
              modalState={modalState}
              location={location}
              nearbyPartnerClinics={nearbyLocations.partners}
              fetchPartnerClinics={fetchPartnerClinics}
              fetchLocationSlotsData={fetchLocationSlotsData}
              handleRequestAppointmentClick={handleRequestAppointmentClick}
              accountSummary={accountSummary}
              login={login}
              suggestions={suggestions}
              reasonForVisit={reasonForVisit}
              setBookingProfile={setBookingProfile}
              newBooking={newBooking}
            />
          )}
        </Layout>
      </Fragment>
    );
  };
}

ClinicDetailPage.contextTypes = {
  setTitle: PropTypes.func.isRequired,
  setMeta: PropTypes.func.isRequired,
};

ClinicDetailPage.propTypes = {
  location: PropTypes.object.isRequired,
  modalState: PropTypes.object,
  newBooking: PropTypes.object,
  viewport: PropTypes.object,
  showModal: PropTypes.func,
  hideModal: PropTypes.func,
  nearbyLocations: PropTypes.object,
  fetchLocationData: PropTypes.func,
  fetchPartnerClinics: PropTypes.func,
  fetchLocationSlotsData: PropTypes.func,
  requestId: PropTypes.string,
  handleRequestAppointmentClick: PropTypes.func,
  accountSummary: PropTypes.object,
  login: PropTypes.object,
  suggestions: PropTypes.array,
  reasonForVisit: PropTypes.string,
  setBookingProfile: PropTypes.func,
  sessionTrackingProperties: PropTypes.object,
};
